import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Chip,
  Card, CardContent,  CircularProgress,
  Alert, MenuItem
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Edit, Delete, Add, LocalBar,
  Visibility, VisibilityOff, RestaurantMenu
} from '@mui/icons-material';
import { categorieBoissonAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const CategoriesBoissonsPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    type_categorie: 'soft',
    description: '',
    est_active: true
  });

  const typeOptions = [
    { value: 'soft', label: 'Boissons non alcoolisées' },
    { value: 'alcool', label: 'Boissons alcoolisées' },
    { value: 'cocktail', label: 'Cocktails' },
    { value: 'autre', label: 'Autre' },
  ];

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categorieBoissonAPI.getAll();
      
      console.log('API Response:', response);
      console.log('Response data:', response.data);
      
      let categoriesData = [];
      
      if (response.data && typeof response.data === 'object') {
        if (response.data.results && Array.isArray(response.data.results)) {
          categoriesData = response.data.results;
        } else if (Array.isArray(response.data)) {
          categoriesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          categoriesData = response.data.data;
        }
      }
      
      setCategories(categoriesData);
      
    } catch (error) {
      console.error('Erreur API:', error);
      setError(`Erreur de chargement: ${error.message || 'Erreur serveur'}`);
      setCategories([]);
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormData({
      nom: '',
      type_categorie: 'soft',
      description: '',
      est_active: true
    });
    setIsEditing(false);
  };

  const handleOpenDialog = (categorie = null) => {
    if (categorie) {
      setFormData(categorie);
      setIsEditing(true);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      console.log('Données envoyées à l\'API:', formData);
      
      const validTypes = typeOptions.map(opt => opt.value);
      if (!validTypes.includes(formData.type_categorie)) {
        toast.error(`Type "${formData.type_categorie}" non valide. Utilisez: ${validTypes.join(', ')}`);
        return;
      }
      
      if (isEditing) {
        await categorieBoissonAPI.update(formData.id, formData);
        toast.success('Catégorie modifiée avec succès');
      } else {
        await categorieBoissonAPI.create(formData);
        toast.success('Catégorie créée avec succès');
      }
      setOpenDialog(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Erreur détaillée:', error);
      console.error('Réponse d\'erreur:', error.response?.data);
      
      let errorMessage = 'Erreur lors de l\'enregistrement';
      
      if (error.response?.data) {
        if (error.response.data.type_categorie) {
          errorMessage = `Type de catégorie: ${Array.isArray(error.response.data.type_categorie) 
            ? error.response.data.type_categorie.join(', ') 
            : error.response.data.type_categorie}`;
        } else if (error.response.data.nom) {
          errorMessage = `Nom: ${Array.isArray(error.response.data.nom) 
            ? error.response.data.nom.join(', ') 
            : error.response.data.nom}`;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await categorieBoissonAPI.delete(id);
        toast.success('Catégorie supprimée avec succès');
        fetchCategories();
      } catch (error) {
        console.error('Erreur:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      // PATCH : un PUT partiel serait rejeté (`nom` obligatoire)
      await categorieBoissonAPI.patch(id, { est_active: !currentStatus });
      toast.success(`Catégorie ${!currentStatus ? 'activée' : 'désactivée'}`);
      fetchCategories();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la modification du statut');
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      soft: 'success',
      alcool: 'error',
      cocktail: 'warning',
      autre: 'default'
    };
    return colors[type] || 'default';
  };

  const getTypeLabel = (type) => {
    const option = typeOptions.find(t => t.value === type);
    return option ? option.label : type;
  };

  const activeCategories = Array.isArray(categories) ? categories.filter(c => c.est_active) : [];
  const softCount = Array.isArray(categories) ? categories.filter(c => c.type_categorie === 'soft').length : 0;
  const alcoolCount = Array.isArray(categories) ? categories.filter(c => c.type_categorie === 'alcool').length : 0;
  const cocktailCount = Array.isArray(categories) ? categories.filter(c => c.type_categorie === 'cocktail').length : 0;

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
        p={3}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button 
              variant="outlined" 
              onClick={fetchCategories}
              size="small"
            >
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        p: 3,
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      {/* Header avec statistiques */}
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <LocalBar sx={{ mr: 2, verticalAlign: 'middle' }} />
              Catégories Boissons
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Gérez les catégories de boissons pour votre carte
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                height: 48,
                px: 3,
                borderRadius: 1
              }}
            >
              Nouvelle Catégorie
            </Button>
          </Grid>
        </Grid>

        {/* Cartes statistiques */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Total Catégories
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {Array.isArray(categories) ? categories.length : 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Actives
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {activeCategories.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Non Alcoolisées
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {softCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Alcoolisées
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {alcoolCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Tableau des catégories */}
      <Paper 
        elevation={2} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Date Création</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(categories) && categories.length > 0 ? (
                categories.map((cat) => (
                  <TableRow key={cat.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ py: 2 }}>
                      <Typography fontWeight="medium">{cat.nom}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={getTypeLabel(cat.type_categorie)}
                        color={getTypeColor(cat.type_categorie)}
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                        {cat.description || 'Aucune description'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={cat.est_active}
                              onChange={() => toggleStatus(cat.id, cat.est_active)}
                              size="small"
                            />
                          }
                          label={
                            <Chip
                              icon={cat.est_active ? <Visibility /> : <VisibilityOff />}
                              label={cat.est_active ? 'Active' : 'Inactive'}
                              size="small"
                              color={cat.est_active ? 'success' : 'default'}
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          }
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2">
                        {cat.date_creation ? new Date(cat.date_creation).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }} align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(cat)}
                          size="small"
                          sx={{ 
                            '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(cat.id)}
                          size="small"
                          sx={{ 
                            '&:hover': { backgroundColor: 'error.light', color: 'white' }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <Typography variant="body1" color="textSecondary">
                        {Array.isArray(categories) && categories.length === 0 
                          ? 'Aucune catégorie trouvée' 
                          : 'Erreur de chargement des données'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Créez votre première catégorie pour commencer
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog d'édition/création */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalBar />
            <Typography variant="h6">
              {isEditing ? 'Modifier la Catégorie' : 'Nouvelle Catégorie'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Nom de la catégorie"
              fullWidth
              value={formData.nom}
              onChange={(e) => setFormData({...formData, nom: e.target.value})}
              required
              error={!formData.nom}
              helperText={!formData.nom ? 'Ce champ est requis' : ''}
              variant="outlined"
              size="small"
            />
            
            <TextField
              select
              label="Type de catégorie"
              fullWidth
              value={formData.type_categorie}
              onChange={(e) => setFormData({...formData, type_categorie: e.target.value})}
              helperText="Sélectionnez le type de boisson"
              variant="outlined"
              size="small"
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Décrivez cette catégorie..."
              variant="outlined"
              size="small"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.est_active}
                  onChange={(e) => setFormData({...formData, est_active: e.target.checked})}
                />
              }
              label={
                <Typography variant="body2">
                  Catégorie active
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            variant="outlined"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.nom}
            sx={{ ml: 1 }}
          >
            {isEditing ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesBoissonsPage;